import { FC, useEffect, useRef, useState } from "react";
import {
  CalendarFieldValue,
  CalendarProps,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { FormGroup } from "@blueprintjs/core";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  CalendarComponent,
  RenderDayCellEventArgs,
  ChangedEventArgs,
} from "@syncfusion/ej2-react-calendars";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { parse, format } from "date-fns";

export interface CalendarFieldProps extends CalendarProps {
  minDate?: Date;
  maxDate?: Date;
  showTodayButton?: boolean;
  disableWeekends?: boolean;
  disabledDates?: Date[];
  /** Will be called before each date displayed on calendar: return true to enable, false to disable */
  dateFilter?: (date: Date) => boolean;
  dateFormat?: string;
  labelProps?: FieldLabeledProps;
}

export const CalendarField: FC<CalendarFieldProps> = (props) => {
  const calendarRef = useRef<CalendarComponent | null>(null);
  const [localDate, setLocalDate] = useState<CalendarFieldValue>();

  useEffect(() => {
    setLocalDate(props.value);
  }, [props.value]);

  const parseValue = (value: CalendarFieldValue): Date => {
    return value
      ? parse(value, props.dateFormat ?? "yyyy-MM-dd", new Date())
      : (null as unknown as Date); // undefined doesn't unset the value
  };

  const formatValue = (value?: Date): CalendarFieldValue => {
    return value ? format(value, props.dateFormat ?? "yyyy-MM-dd") : "";
  };

  const handleRenderDayCell = (args: RenderDayCellEventArgs) => {
    const date = args.date;
    if (!date) {
      return;
    }
    if (
      // Disable Saturdays and Sundays if disableWeekends is true
      (props.disableWeekends && (date.getDay() === 0 || date.getDay() === 6)) ||
      // Disable dates included in disabledDates array
      props.disabledDates?.some(
        (d) =>
          d.getDate() === date.getDate() &&
          d.getMonth() === date.getMonth() &&
          d.getFullYear() === date.getFullYear()
      ) ||
      // Disable dates based on dateFilter
      (props.dateFilter && !props.dateFilter(date))
    ) {
      args.isDisabled = true;
    }
  };

  const handleChange = (args: ChangedEventArgs) => {
    const formattedValue = formatValue(args.value);
    setLocalDate(formattedValue);
    props.onSubmit && props.onSubmit(formattedValue);
  };

  return (
    <div className="ucl-base-input-calendar-field-container">
      <FormGroup
        className={`${
          props.isRequired && !localDate
            ? "has-required-background"
            : "base-field"
        }`}
      >
        {props.label && (
          <FieldLabel
            labelName={props.label}
            tooltipDescription={props.description}
            fieldProps={{ ...props, value: localDate }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
        )}
        <CalendarComponent
          ref={calendarRef}
          min={props.minDate}
          max={props.maxDate}
          showTodayButton={props.showTodayButton}
          renderDayCell={handleRenderDayCell}
          value={parseValue(localDate)}
          change={handleChange}
          enabled={!props.disabled}
        />
        {props.errorMessages && (
          <FormFieldErrorContainer errorMessages={props.errorMessages} />
        )}
      </FormGroup>
    </div>
  );
};
